import { parseStepToolActivity } from 'app/pages/loanApplication/tabs/aiActivity/ExecutionDetailDrawer/StepDetailPane/StepToolCalls.utils';
import { resolveActionStepTypeFromToolName } from 'app/pages/loanApplication/tabs/aiChat/components/ActionPlan/ActionPlan.constants';

import { ActionStepType } from 'constants/aiChat';

const EMPTY_RUN_TOOL_STATS = Object.freeze({
  toolCalls: 0,
  guideline: 0,
  documents: 0,
  recordsUpdated: 0,
  recordsRead: 0,
  agents: 0,
  skills: 0,
});

// A document-read tool exposes the documents collection (e.g. `get_documents`,
// `get_document`). Matching the raw tool id on `document`/`doc` keeps this
// honest — it only counts calls that actually touched documents.
const isDocumentToolName = toolName => {
  const normalized = toolName.toLowerCase();

  return normalized.includes('document') || normalized.includes('doc');
};

const classifyToolCall = (stats, toolCall) => {
  const { toolName } = toolCall;
  const isDocument = isDocumentToolName(toolName);
  const stepType = resolveActionStepTypeFromToolName(toolName);

  return {
    toolCalls: stats.toolCalls + 1,
    guideline: stats.guideline + (stepType === ActionStepType.GUIDELINE_LOOKUP ? 1 : 0),
    documents: stats.documents + (isDocument ? 1 : 0),
    recordsUpdated: stats.recordsUpdated + (stepType === ActionStepType.MODIFYING_RECORD ? 1 : 0),
    // A document read is already surfaced as "Documents reviewed", so it is not
    // double-counted under "Records read".
    recordsRead:
      stats.recordsRead + (stepType === ActionStepType.DATA_READING && !isDocument ? 1 : 0),
  };
};

// Aggregates run-wide tool activity from each step's already-loaded messages.
// `stepsMessages` is an array of message arrays (one per step); steps whose
// messages are not loaded yet simply contribute nothing, so the counts converge
// as the drawer eager-loads every step.
//
// Agents and skills are counted as DISTINCT tool names (how many unique
// invoke_agent_* / use_skill_* were invoked) rather than raw call counts,
// so a single agent called three times contributes 1 to the tally.
export const computeRunToolStats = stepsMessages => {
  if (!Array.isArray(stepsMessages)) {
    return EMPTY_RUN_TOOL_STATS;
  }

  const allToolCalls = stepsMessages.flatMap(messages => parseStepToolActivity(messages).toolCalls);

  const baseStats = allToolCalls.reduce(classifyToolCall, {
    toolCalls: 0,
    guideline: 0,
    documents: 0,
    recordsUpdated: 0,
    recordsRead: 0,
  });

  const agentNames = allToolCalls
    .map(tc => tc.toolName?.toLowerCase() ?? '')
    .filter(name => name.startsWith('invoke_agent_'));

  const skillNames = allToolCalls
    .map(tc => tc.toolName?.toLowerCase() ?? '')
    .filter(name => name.startsWith('use_skill_'));

  return {
    ...baseStats,
    agents: new Set(agentNames).size,
    skills: new Set(skillNames).size,
  };
};
